import qs from "qs";
import axios from "axios";
import AjaxFormat from "./utils/AjaxFormat";
import JsonBig from "json-bigint";
import { navigateToLogin } from "./utils/navigation";
import i18n from "./i18n/config";

// 保存原始的 JSON.stringify 方法
const originalStringify = JSON.stringify;

// 覆盖 JSON.stringify 方法
JSON.stringify = function (value, replacer, space) {
  // 自定义的 replacer 函数来处理 BigInt 类型
  const customReplacer = (key, value) => {
    if (typeof value === "bigint") {
      return value.toString(); // 将 BigInt 转换为字符串
    }
    return value;
  };

  // 如果 replacer 已经被提供，则组合自定义的 replacer 和已有的 replacer
  const finalReplacer =
    Array.isArray(replacer) || typeof replacer === "function"
      ? (key, value) => customReplacer(key, replacer.call(this, key, value))
      : customReplacer;

  // 使用原始的 stringify 方法，并应用自定义的 replacer
  return originalStringify.call(this, value, finalReplacer, space);
};

// 返回参数格式化
const axiosFormat = new AjaxFormat();

// 请求参数格式化
const requestAxiosFormat = new AjaxFormat();

// 配置 GET 请求 a=[1,2] ==> a=1,2
axios.defaults.paramsSerializer = (params) =>
  qs.stringify(params, { arrayFormat: "comma" });

axios.defaults.transformResponse = [
  function (data) {
    // 对接收的 data 进行任意转换处理
    if (typeof data === "string") {
      try {
        data = JsonBig({
          storeAsString: true,
          useNativeBigInt: true,
        }).parse(data);
      } catch (e) {
        // 处理解析错误
      }
    }

    return data;
  },
];

/**
 * 识别是否为网络错误（请求未到达服务端）
 */
function isNetworkLikeError(e) {
  return (
    !e?.response ||
    e?.code === "ERR_NETWORK" ||
    e?.code === "ECONNABORTED" ||
    e?.message === "Network Error"
  );
}

/**
 * 获取网络错误的国际化文案。
 * 优先用 i18n.t 读取 error.networkConnectionFailed，
 * 当 i18n 尚未就绪或翻译缺失时，按当前语言映射到中/英/泰硬编码文案兜底，
 * 避免拦截器把 i18n key 本身（如 "error.networkConnectionFailed"）当文案返回。
 */
const NETWORK_ERROR_KEY = "error.networkConnectionFailed";
const NETWORK_ERROR_FALLBACK = {
  zh: "网络连接失败",
  en: "Network connection failed",
  th: "การเชื่อมต่อเครือข่ายล้มเหลว",
};

function getNetworkErrorMessage() {
  let text;
  try {
    text = i18n?.t?.(NETWORK_ERROR_KEY);
  } catch (_) {
    text = undefined;
  }
  if (text && text !== NETWORK_ERROR_KEY) {
    return text;
  }
  const rawLang =
    i18n?.language ||
    (typeof navigator !== "undefined" ? navigator.language : "") ||
    "en";
  const langCode = String(rawLang).toLowerCase().split(/[-_]/)[0];
  return NETWORK_ERROR_FALLBACK[langCode] || NETWORK_ERROR_FALLBACK.en;
}

/**
 * 构造统一的网络错误伪响应（与后端 success=false 响应结构保持一致）
 * 业务层的 if (response.success) 会走 else 分支，自动展示 errorMessage，
 * 从而在请求位点提示“Network connection failed”，不会双重提示。
 */
function buildNetworkErrorResponse() {
  return {
    success: false,
    isNetworkError: true,
    errorCode: "NETWORK_ERROR",
    errorMessage: getNetworkErrorMessage(),
    flag: "fail",
    traceId: null,
    msg: null,
    code: null,
    message: null,
    data: null,
  };
}

/**
 * 统一请求拦截器
 */
axios.interceptors.response.use(
  (res) => {
    if (res.status === 200) {
      const data = res.data || {};
      if (data.success) {
        return Promise.resolve(data);
      } else {
        // 登录态过期 需要跳转到登录界面
        if (
          data &&
          (data.errorCode === "SC-A4010" || data.errorCode === 401000)
        ) {
          navigateToLogin();
        }
        // 业务失败直接把 success=false 的完整 response 返回给业务层，
        // 由业务层根据 response.errorMessage 进行弹框提示，catch 只处理网络/HTTP 异常
        return Promise.resolve(data);
      }
    }

    return Promise.reject(
      new Error(`HTTP Status ${res.status} by: ${res.config.url}`)
    );
  },
  (e) => {
    // 识别网络异常：请求未到达服务端 / 超时 / DNS 失败等
    // 统一转为 success=false 伪响应，让业务 else 分支自动弹出 errorMessage（“Network connection failed”）。
    if (isNetworkLikeError(e)) {
      return Promise.resolve(buildNetworkErrorResponse());
    }
    return Promise.reject(e);
  }
);

// /**
//  * 返回数据格式化
//  */
// // 做format
// axios.interceptors.response.use(
//   (res) => {
//     return axiosFormat.format(res);
//   },
//   (e) => Promise.reject(e),
// );

// // extends 格式化 ajax 返回值
// axios.interceptors.format = function (path, format) {
//   return axiosFormat.add(path, format);
// };

/**
 * 请求参数格式化
 * requestConfig 是axios请求对象
 */
axios.interceptors.request.use(
  (requestConfig) => {
    return requestAxiosFormat.format(requestConfig);
  },
  (e) => Promise.reject(e)
);

// extends 格式化 ajax 请求参数
axios.interceptors.requestFormat = function (path, format) {
  return requestAxiosFormat.add(path, format);
};
